import React from 'react';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getTrialRemainingDays, isPremiumWithGrace, isUserTrial } from 'commons/utils/accountUtils';
import moment from 'moment';
import { currentUserSelector, useAppStore } from 'stores/appStore';
import { setIsPaymentModalOpenSelector, usePaymentModalOpenStore } from 'stores/paymentModalStore';

const useStyles = makeStyles()((theme) => ({
  upgradeButton: {
    boxShadow: '0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.20)',
    padding: '6px 16px',
    borderRadius: '4px',
    backgroundColor: '#0fbd6e',
    '&:hover': {
      backgroundColor: theme.palette.success.main,
    },
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },

  upgradeButtonTitle: {
    color: 'white',
    lineHeight: '1.35',
    fontSize: '13px',
  },
  titleTrialRemaining: {
    backgroundColor: theme.palette.warning.main,
    padding: '0px 8px',
    borderRadius: '4px',
    color: theme.palette.error.contrastText,
    fontSize: '11px',
  },
  titleTrialRemainingError: {
    backgroundColor: theme.palette.error.main,
    padding: '0px 8px',
    borderRadius: '4px',
    color: theme.palette.error.contrastText,
    fontSize: '11px',
  },
  titleTrialRemainingExpired: {
    backgroundColor: theme.palette.error.main,
    padding: '0px 8px',
    borderRadius: '4px',
    color: theme.palette.error.contrastText,
    fontSize: '11px',
  },
  // BLACKFRIDAY
  discountChipBlackFriday: {
    padding: '5px 15px',
    background: 'linear-gradient(45deg, #00ffa1 0%, #5721E5 100%)',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    boxShadow: '0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.20)',
    marginRight: '10px',
    '&:hover': {
      background: 'linear-gradient(45deg, #16bf81 0%, #5721E5 100%)',
    },
    cursor: 'pointer',
  },
  discountBannerText: {
    marginTop: '5px',
    color: 'white',
    fontSize: '13px',
    fontWeight: 'bold',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  discountBannerTextHighlight: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: 'yellow',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  discountBannerTextHighlightNewYear: {
    fontSize: '13px',
    fontWeight: 'bold',

    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    borderRadius: '5px',
    backgroundColor: '#5fd118',
    padding: '1px 5px',
  },
  discountBlackFridayText: {
    marginTop: '5px',
    color: 'white',
    fontSize: '13px',
    fontWeight: 'bold',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  discountBlackFridayText1: {
    color: 'white',
    fontSize: '16px',
    fontWeight: 'bold',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  discountBlackFridayText2: {
    color: 'black',
    fontSize: '16px',
    fontWeight: 'bold',
    textShadow:
      '1px 0 0 #fff, -1px 0 0 #fff, 0 1px 0 #fff, 0 -1px 0 #fff, 1px 1px #fff, -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  discountLimitedTimeText: {
    color: 'white',
    fontSize: '10px',
    textAlign: 'center',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
}));

function getMessageForNumberOfDays(trialRemainingNumDays) {
  if (trialRemainingNumDays != null && trialRemainingNumDays >= 0) {
    return trialRemainingNumDays === 0
      ? 'Seu período de teste expira hoje.'
      : `Seu período de teste expira em ${trialRemainingNumDays} ${
          trialRemainingNumDays === 1 ? 'dia' : 'dias'
        }.`;
  }
  return 'Seu período de teste expirou';
}

const IS_BLACK_FRIDAY = moment().month() === 10;
const IS_NEW_YEAR = !IS_BLACK_FRIDAY;

// COMPONENT
function HeaderNotPremium() {
  const { classes } = useStyles();
  const currentUser = useAppStore(currentUserSelector);
  const trialRemainingDays = getTrialRemainingDays(currentUser);
  const premiumWithGrace = isPremiumWithGrace(currentUser);
  const setIsPaymentModalOpen = usePaymentModalOpenStore(setIsPaymentModalOpenSelector);

  const isTrial = isUserTrial(currentUser);

  return (
    <>
      {isTrial && IS_BLACK_FRIDAY && (
        <div
          className={classes.discountChipBlackFriday}
          onClick={() => {
            setIsPaymentModalOpen(true);
          }}
        >
          <div>
            <span className={classes.discountBlackFridayText1} component="span">
              BLACK{' '}
            </span>
            <span className={classes.discountBlackFridayText2} component="span">
              FRIDAY
            </span>
          </div>
          <div>
            <span className={classes.discountBannerText} component="span">
              Resgate{' '}
              <span className={classes.discountBannerTextHighlight} component="span">
                50% de desconto
              </span>
            </span>
          </div>
          <p className={classes.discountLimitedTimeText} component="span">
            Válido apenas esta semana
          </p>
        </div>
      )}
      {isTrial && IS_NEW_YEAR && (
        <div
          className={classes.discountChipBlackFriday}
          onClick={() => {
            setIsPaymentModalOpen(true);
          }}
        >
          <div>
            <span className={classes.discountBlackFridayText1} component="span">
              Promoção ANO{' '}
            </span>
            <span className={classes.discountBlackFridayText2} component="span">
              NOVO
            </span>{' '}
            🥂
          </div>
          <div>
            {currentUser?.principal?.maxNumberUsers === 1 || currentUser?.principal?.maxNumberUsers === 3 ? (
              <>
                <span className={classes.discountBannerText} component="span">
                  Resgate{' '}
                  <span className={classes.discountBannerTextHighlightNewYear} component="span">
                    45% de desconto
                  </span>
                </span>
              </>
            ) : null}
            {currentUser?.principal?.maxNumberUsers === 6 ? (
              <>
                <span className={classes.discountBannerText} component="span">
                  Resgate{' '}
                  <span className={classes.discountBannerTextHighlightNewYear} component="span">
                    45% de desconto
                  </span>
                </span>
              </>
            ) : null}
            {currentUser?.principal?.maxNumberUsers === 9 ? (
              <>
                <span className={classes.discountBannerText} component="span">
                  Resgate{' '}
                  <span className={classes.discountBannerTextHighlightNewYear} component="span">
                    40% de desconto
                  </span>
                </span>
              </>
            ) : null}
          </div>
          <p className={classes.discountLimitedTimeText} component="span">
            Válida apenas até {moment().format('MMMM [de] YYYY')}
          </p>
        </div>
      )}
      <div
        className={classes.upgradeButton}
        onClick={() => {
          setIsPaymentModalOpen(true);
        }}
      >
        <Typography component="span" variant="button" className={classes.upgradeButtonTitle}>
          Ative a sua conta premium.
        </Typography>
        {isTrial && (
          <>
            {trialRemainingDays != null ? (
              <Typography
                component="span"
                variant="body2"
                className={
                  trialRemainingDays <= 3 ? classes.titleTrialRemainingError : classes.titleTrialRemaining
                }
              >
                {getMessageForNumberOfDays(trialRemainingDays)}
              </Typography>
            ) : (
              <Typography component="span" variant="body2" className={classes.titleTrialRemainingExpired}>
                {premiumWithGrace ? 'Sua assinatura expirou' : 'Seu período de teste expirou'}
              </Typography>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default React.memo(HeaderNotPremium);
